import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, Trash2, ExternalLink, History, ThumbsUp, Award, ArrowUpDown, ArrowUp, ArrowDown, Archive, ArchiveRestore } from 'lucide-react';
import { format } from 'date-fns';

export default function ArtifactTableView({
  artifacts,
  officers,
  user,
  isSeniorOfficer,
  onEdit,
  onDelete,
  onViewHistory,
  onApprove,
  onInstitutionalize,
  onArchive,
  onUnarchive,
  showArchived,
  deleteConfirm
}) {
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-indigo-400" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-indigo-400" />;
  };

  const statusColors = {
    draft: 'bg-slate-500/20 text-slate-400',
    pending_approval: 'bg-amber-500/20 text-amber-400',
    approved: 'bg-emerald-500/20 text-emerald-400',
    institutionalized: 'bg-indigo-500/20 text-indigo-400'
  };

  const sorted = useMemo(() => {
    return [...artifacts].sort((a, b) => {
      let valA, valB;
      switch (sortKey) {
        case 'name':
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
          break;
        case 'category':
          valA = (a.category || '').toLowerCase();
          valB = (b.category || '').toLowerCase();
          break;
        case 'status':
          valA = (a.status || '').toLowerCase();
          valB = (b.status || '').toLowerCase();
          break;
        case 'owner':
          const ownerA = officers.find(o => o.user_email === a.owner_email);
          const ownerB = officers.find(o => o.user_email === b.owner_email);
          valA = (ownerA?.callsign || a.owner_email || '').toLowerCase();
          valB = (ownerB?.callsign || b.owner_email || '').toLowerCase();
          break;
        case 'type':
          valA = (a.type || '').toLowerCase();
          valB = (b.type || '').toLowerCase();
          break;
        case 'created':
          valA = a.created_date ? new Date(a.created_date).getTime() : 0;
          valB = b.created_date ? new Date(b.created_date).getTime() : 0;
          break;
        case 'updated':
          valA = a.updated_date ? new Date(a.updated_date).getTime() : 0;
          valB = b.updated_date ? new Date(b.updated_date).getTime() : 0;
          break;
        default:
          valA = '';
          valB = '';
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [artifacts, sortKey, sortDir, officers]);

  return (
    <div className="border border-slate-800 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                <span className="flex items-center">Name <SortIcon column="name" /></span>
              </TableHead>
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('category')}>
                <span className="flex items-center">Category <SortIcon column="category" /></span>
              </TableHead>
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('type')}>
                <span className="flex items-center">Type <SortIcon column="type" /></span>
              </TableHead>
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('status')}>
                <span className="flex items-center">Status <SortIcon column="status" /></span>
              </TableHead>
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('owner')}>
                <span className="flex items-center">Owner <SortIcon column="owner" /></span>
              </TableHead>
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('created')}>
                <span className="flex items-center">Created <SortIcon column="created" /></span>
              </TableHead>
              <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('updated')}>
                <span className="flex items-center">Updated <SortIcon column="updated" /></span>
              </TableHead>
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((artifact) => {
              const owner = officers.find(o => o.user_email === artifact.owner_email);
              const canEdit = artifact.owner_email === user?.email || (artifact.editors || []).includes(user?.email) || isSeniorOfficer;

              return (
                <TableRow key={artifact.id} className="border-slate-800 hover:bg-slate-800/30">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-200 font-medium">{artifact.name}</span>
                      {artifact.file_url && (
                        <a href={artifact.file_url} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    {artifact.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {artifact.tags.slice(0, 3).map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-[10px] py-0 px-1.5 h-4 text-slate-50">#{tag}</Badge>
                        ))}
                        {artifact.tags.length > 3 && <span className="text-[10px] text-slate-500">+{artifact.tags.length - 3}</span>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs text-slate-50">{artifact.category}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400 capitalize">{artifact.type}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge className={`${statusColors[artifact.status]} border-none text-xs`}>
                        {artifact.status?.replace('_', ' ')}
                      </Badge>
                      {artifact.archived && <Badge className="bg-amber-500/20 text-amber-400 border-none text-xs">Archived</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400">
                    {owner?.callsign || artifact.owner_email}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {artifact.created_date && format(new Date(artifact.created_date), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {artifact.updated_date && format(new Date(artifact.updated_date), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {isSeniorOfficer && artifact.status === 'draft' && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-400 hover:text-emerald-300" onClick={() => onApprove(artifact.id)} title="Approve">
                          <ThumbsUp className="w-3 h-3" />
                        </Button>
                      )}
                      {isSeniorOfficer && artifact.status === 'approved' && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-indigo-400 hover:text-indigo-300" onClick={() => onInstitutionalize(artifact.id)} title="Institutionalize">
                          <Award className="w-3 h-3" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onViewHistory(artifact)} title="Version history">
                        <History className="w-3 h-3" />
                      </Button>
                      {canEdit && (
                        <>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(artifact)}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <div className="relative">
                            <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-red-400" onClick={() => deleteConfirm.checkAndConfirm(artifact.id, () => onDelete(artifact.id))}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            {deleteConfirm.isPendingConfirm(artifact.id) && (
                              <span className="absolute right-0 -bottom-8 text-xs text-red-400 bg-slate-900 px-2 py-1 rounded border border-red-500/30 whitespace-nowrap z-10">
                                Click again
                              </span>
                            )}
                          </div>
                        </>
                      )}
                      {canEdit && (
                        artifact.archived ? (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-amber-400 hover:text-amber-300" onClick={() => onUnarchive(artifact.id)} title="Unarchive">
                            <ArchiveRestore className="w-3 h-3" />
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-amber-400" onClick={() => onArchive(artifact.id)} title="Archive">
                            <Archive className="w-3 h-3" />
                          </Button>
                        )
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}